import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Edit2,
  History,
  Loader2,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  Trash2
} from 'lucide-react';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import useAuthRoles from '../../../hooks/useAuthRoles';
import useAbortController from '../../../hooks/useAbortController';
import Button from '../../../components/Button/Button';
import { Button as IconButton } from '../../../components/ui/button';
import NotificationSnackbar from '../../../components/NotificationSnackbar/NotificationSnackbar';
import { Badge } from '../../../components/ui/badge';
import { TableCell } from '../../../components/ui/table';
import DataTable from '../../../components/DataTable/DataTable';
import { HeaderDefinition } from '../../../components/DataTable/DataTableHeader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../../../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../../components/ui/tooltip';
import WebsiteSourceService from '../../../services/WebsiteSourceService/WebsiteSourceService';
import { WebsiteSourceResponse } from '../../../types/WebsiteSource';
import WebsiteSourceDialog, { apiErrorMessage } from './WebsiteSourceDialog';
import RunHistorySheet from './RunHistorySheet';
import {
  formatDateTime,
  formatInterval,
  formatRelative,
  runStatusVariant
} from './websiteFormatting';

const POLL_INTERVAL_MS = 10000;

const websiteHeaders: HeaderDefinition[] = [
  { key: 'seedUrl', label: 'Website', sortable: true, className: 'w-[34%]' },
  { key: 'status', label: 'Status', className: 'w-[12%]' },
  { key: 'pageCount', label: 'Pages', sortable: true, className: 'w-[10%]' },
  {
    key: 'intervalHours',
    label: 'Schedule',
    sortable: true,
    className: 'w-[12%]'
  },
  {
    key: 'lastRunFinishedAt',
    label: 'Last run',
    sortable: true,
    className: 'w-[12%]'
  },
  { key: 'nextRunAt', label: 'Next run', sortable: true, className: 'w-[12%]' },
  { key: 'actions', label: 'Actions', className: 'w-[8%]' }
];

function WebsitesTab(): React.ReactElement {
  const location = useLocation();
  const { currentChatbot } = useContext(ChatbotContext);
  const { userCanWrite } = useAuthRoles();
  const { getAbortSignal } = useAbortController();

  const [sources, setSources] = useState<WebsiteSourceResponse[] | null>(null);
  const [numberOfResults, setNumberOfResults] = useState(0);
  const [page, setPage] = useState(0);
  const [resultsPerPage, setResultsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState('createdDate');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WebsiteSourceResponse | null>(null);
  const [historyFor, setHistoryFor] = useState<WebsiteSourceResponse | null>(
    null
  );
  const [notice, setNotice] = useState<{
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  const fetchSources = useCallback(async (): Promise<void> => {
    if (!currentChatbot) return;
    try {
      const data = await WebsiteSourceService.list(
        currentChatbot.id,
        { page, resultsPerPage, sortColumn, sortDirection },
        getAbortSignal()
      );
      if (data.results.length === 0 && page > 0) {
        setPage(page - 1);
        return;
      }
      setSources(data.results);
      setNumberOfResults(data.numberOfResults);
      setHasError(false);
    } catch (err) {
      if (axios.isCancel(err)) return;
      setSources([]);
      setNumberOfResults(0);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentChatbot,
    page,
    resultsPerPage,
    sortColumn,
    sortDirection,
    getAbortSignal
  ]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  useEffect(() => {
    const anyRunning = sources?.some((source) => source.running);
    if (!anyRunning) return undefined;
    const interval = setInterval(fetchSources, POLL_INTERVAL_MS);
    return (): void => clearInterval(interval);
  }, [sources, fetchSources]);

  const notify = (
    message: string,
    severity: 'success' | 'error' = 'error'
  ): void => setNotice({ message, severity });

  const handleToggleEnabled = async (
    source: WebsiteSourceResponse
  ): Promise<void> => {
    if (!currentChatbot) return;
    try {
      const updated = await WebsiteSourceService.setEnabled(
        currentChatbot.id,
        source.id,
        !source.enabled
      );
      setSources(
        (prev) => prev?.map((s) => (s.id === updated.id ? updated : s)) ?? null
      );
    } catch (err) {
      notify(
        apiErrorMessage(err, 'Unable to update the website. Please try again.')
      );
    }
  };

  const handleDelete = async (source: WebsiteSourceResponse): Promise<void> => {
    if (!currentChatbot) return;
    try {
      await WebsiteSourceService.delete(currentChatbot.id, source.id);
      notify('Website and its pages deleted.', 'success');
      fetchSources();
    } catch (err) {
      notify(
        apiErrorMessage(err, 'Unable to delete the website. Please try again.')
      );
    }
  };

  const handleSaved = (): void => {
    notify('Scheduled; the first crawl starts within a minute.', 'success');
    fetchSources();
  };

  if (!userCanWrite()) {
    return <Navigate to='/chats' state={{ from: location }} replace />;
  }

  const renderStatus = (source: WebsiteSourceResponse): React.ReactElement => {
    if (!source.enabled) {
      return <Badge variant='outline'>Paused</Badge>;
    }
    if (source.running) {
      return (
        <Badge variant='secondary'>
          <Loader2 className='mr-1 h-3 w-3 animate-spin' /> Crawling
        </Badge>
      );
    }
    if (!source.lastRunStatus) {
      return <Badge variant='secondary'>Scheduled</Badge>;
    }
    return (
      <Badge variant={runStatusVariant(source.lastRunStatus)}>
        {source.lastRunStatus}
      </Badge>
    );
  };

  return (
    <div className='flex flex-1 flex-col'>
      <Helmet>
        <title>Japeto - Websites</title>
      </Helmet>
      <div className='mb-4 flex items-center justify-between gap-4'>
        <p className='text-sm text-muted-foreground'>
          Websites are crawled on a schedule into the knowledge base. Pages
          appear as knowledge base items and are refreshed when they change.
        </p>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          icon={<Plus size='16' />}
        >
          Add website
        </Button>
      </div>

      <NotificationSnackbar
        message={notice?.message ?? ''}
        severity={notice?.severity ?? 'error'}
        open={notice !== null}
        onClose={() => setNotice(null)}
      />

      <TooltipProvider>
        <DataTable<WebsiteSourceResponse>
          searchData={sources}
          numberOfResults={numberOfResults}
          resultsPerPage={resultsPerPage}
          isLoading={isLoading}
          setResultsPerPage={setResultsPerPage}
          setPage={setPage}
          page={page}
          tableHeaders={websiteHeaders}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          hasError={hasError}
          handleFetch={fetchSources}
          noDataTitle='No websites yet'
          noDataMessage='Add one to keep your knowledge base in sync with a site.'
          renderRow={(source) => (
            <>
              <TableCell className='w-[34%]'>
                <a
                  href={source.seedUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='block truncate text-sm underline-offset-2 hover:underline'
                  title={source.seedUrl}
                >
                  {source.seedUrl}
                </a>
                {source.rules.length > 0 && (
                  <span className='text-xs text-muted-foreground'>
                    {source.rules.length} exclusion
                    {source.rules.length === 1 ? '' : 's'}
                  </span>
                )}
              </TableCell>
              <TableCell className='w-[12%]'>{renderStatus(source)}</TableCell>
              <TableCell className='w-[10%]'>{source.pageCount}</TableCell>
              <TableCell className='w-[12%]'>
                {formatInterval(source.intervalHours)}
              </TableCell>
              <TableCell className='w-[12%]'>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      {source.lastRunFinishedAt
                        ? formatRelative(source.lastRunFinishedAt)
                        : 'Never'}
                    </span>
                  </TooltipTrigger>
                  {source.lastRunFinishedAt && (
                    <TooltipContent>
                      {formatDateTime(source.lastRunFinishedAt)}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TableCell>
              <TableCell className='w-[12%]'>
                {source.enabled ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>{formatRelative(source.nextRunAt)}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {formatDateTime(source.nextRunAt)}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <span className='text-muted-foreground'>Paused</span>
                )}
              </TableCell>
              <TableCell className='w-[8%]'>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      variant='ghost'
                      className='h-8 w-8 p-0'
                      aria-label={`Actions for ${source.seedUrl}`}
                    >
                      <MoreHorizontal className='h-4 w-4' />
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem
                      onClick={() => {
                        setEditing(source);
                        setDialogOpen(true);
                      }}
                    >
                      <Edit2 className='mr-2 h-4 w-4' /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setHistoryFor(source)}>
                      <History className='mr-2 h-4 w-4' /> Crawl history
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleToggleEnabled(source)}
                    >
                      {source.enabled ? (
                        <>
                          <Pause className='mr-2 h-4 w-4' /> Pause
                        </>
                      ) : (
                        <>
                          <Play className='mr-2 h-4 w-4' /> Resume
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          <Trash2 className='mr-2 h-4 w-4' /> Delete
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Delete this website?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Every page crawled from {source.seedUrl} (
                            {source.pageCount} pages) is removed from the
                            knowledge base. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(source)}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </>
          )}
        />
      </TooltipProvider>

      <WebsiteSourceDialog
        isOpen={dialogOpen}
        source={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />
      <RunHistorySheet
        source={historyFor}
        onClose={() => setHistoryFor(null)}
      />
    </div>
  );
}

export default WebsitesTab;
